import {
  createTranscribeJobName,
  environment,
} from "../../config/environment";
import { customFetch } from "../../utils/Auth";
import {
  fetchSummarizeJob,
  fetchTranscribeJob,
  normalizeTranscriptWords,
} from "../../utils/secure-job-api";
import {
  buildMergeAudioChunksEvent,
  fetchPatientConversationIds,
} from "../../utils/conversationIds";

export async function getPatientData(id) {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/patients/id/${id}`,
      { method: "GET" }
    );
    return await response.json();
  } catch {
    return { error: "Failed to fetch patient data" };
  }
}

export async function getPatientsByMrn(mrn) {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/patients/summary/mrn/${mrn}`,
      { method: "GET" }
    );
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function getAllDoctors() {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/doctors/all-doctors`,
      { method: "GET" }
    );
    const data = await response.json();
    if (!response.ok) {
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function getDoctorByUserId(userId) {
  if (!userId) return { error: "No doctor user ID on patient record" };
  try {
    const response = await customFetch(
      `${environment.apiUrl}/doctors/doctor-by-user-id/${encodeURIComponent(userId)}`,
      { method: "GET" }
    );
    const data = await response.json();
    if (!response.ok) {
      return { error: data.error || "Doctor not found" };
    }
    return data;
  } catch {
    return { error: "Failed to fetch doctor data" };
  }
}

export async function getSmartChartByPatient(patientId) {
  if (!patientId) {
    return { error: "No patient ID" };
  }

  try {
    const response = await customFetch(
      `${environment.apiUrl}/patients/smart-chart/by-patient/${encodeURIComponent(patientId)}`,
      { method: "GET" }
    );
    const data = await response.json();

    if (!response.ok || !data.success) {
      return { error: data.error || "Failed to fetch smart chart data" };
    }

    return data;
  } catch {
    return { error: "Failed to fetch smart chart data" };
  }
}

/**
 * Full transcribe job from Dynamo API (same as Review Transcript → Get Job).
 * @see client/src/components/ReviewTranscript/index.js — fetchJobInfo
 */
export async function getTranscribeJobByName(transcribeJobName) {
  if (!transcribeJobName) return { error: "No transcribe job name" };
  try {
    return await fetchTranscribeJob(transcribeJobName);
  } catch (error) {
    return { error: error.message || "Failed to fetch transcribe job" };
  }
}

/**
 * Playback URL for a transcribe job. Review Transcript uses this endpoint; the
 * Lambda response's audio_url is pre-signed for S3. Do not use audio_url from
 * /patients/transcribe-jobs/by-patient — that list returns the raw bucket URL.
 */
export async function getTranscribeJobAudioUrl(transcribeJobName) {
  const job = await getTranscribeJobByName(transcribeJobName);
  if (job.error) return { error: job.error };
  if (!job.audio_url) return { error: "No audio URL on job record" };
  return { audio_url: job.audio_url };
}

/**
 * Timed transcript segments for a job, used to sync the transcript with audio.
 * `unavailable` means the job predates segment export — callers should fall back
 * to the plain transcript text rather than treating it as an error.
 * @returns {Promise<{segments: {start: number, end?: number, text: string}[], unavailable?: boolean} | {error: string}>}
 */
export async function getTranscribeJobSegments(transcribeJobName) {
  if (!transcribeJobName) return { error: "No transcribe job name" };
  try {
    const response = await customFetch(
      `${environment.apiUrl}/patients/transcribe-jobs/${encodeURIComponent(
        transcribeJobName
      )}/segments`,
      { method: "GET" }
    );
    if (!response.ok) {
      return { error: "Failed to fetch transcript segments" };
    }
    const data = await response.json();
    return {
      segments: normalizeTranscriptWords(data?.segments),
      unavailable: Boolean(data?.unavailable),
    };
  } catch {
    return { error: "Failed to fetch transcript segments" };
  }
}

export async function getTranscribeJobsByPatient(patientId) {
  if (!patientId) return { error: "No patient ID" };
  try {
    const response = await customFetch(
      `${environment.apiUrl}/patients/transcribe-jobs/by-patient/${patientId}`,
      { method: "GET" }
    );
    if (!response.ok) {
      return { error: "Failed to fetch transcription jobs" };
    }
    return await response.json();
  } catch {
    return { error: "Failed to fetch transcription jobs" };
  }
}

/**
 * Full summarize job from Dynamo API (same as Review Note → fetchJobInfo).
 * @see client/src/components/ReviewNote/index.js
 */
export async function getSummarizeJobByName(summarizeJobName) {
  if (!summarizeJobName) return { error: "No summarize job name" };
  try {
    return await fetchSummarizeJob(summarizeJobName);
  } catch (error) {
    return { error: error.message || "Failed to fetch summarize job" };
  }
}

export async function getSummarizeJobsByPatient(patientId) {
  if (!patientId) return { error: "No patient ID" };
  try {
    const response = await customFetch(
      `${environment.apiUrl}/patients/summarize-jobs/by-patient/${patientId}`,
      { method: "GET" }
    );
    if (!response.ok) {
      return { error: "Failed to fetch summarization jobs" };
    }
    return await response.json();
  } catch {
    return { error: "Failed to fetch summarization jobs" };
  }
}

/** Fetch default template by name (version optional on upstream API). */
export async function getTemplateByName(name) {
  if (!name?.trim()) return { error: "No template name" };
  try {
    const response = await customFetch(
      `${environment.apiUrl}/templates/get-template?name=${encodeURIComponent(name.trim())}`,
      { method: "GET" }
    );
    const data = await response.json();
    if (!response.ok) {
      return {
        error:
          typeof data === "string"
            ? data
            : data?.error || data?.details || "Failed to fetch template",
      };
    }
    return data;
  } catch {
    return { error: "Failed to fetch template" };
  }
}

export async function getTraceByPatient(patientId) {
  if (!patientId) return { error: "No patient ID" };
  try {
    const response = await customFetch(
      `${environment.apiUrl}/trace/fetch`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patient_id: patientId }),
      }
    );
    if (!response.ok) {
      return { error: "Failed to fetch trace data" };
    }
    return await response.json();
  } catch {
    return { error: "Failed to fetch trace data" };
  }
}

export async function updatePatientField(patientId, field, value) {
  if (!patientId) {
    return { success: false, error: "No patient ID" };
  }
  try {
    const response = await customFetch(
      `${environment.apiUrl}/patients/update-field`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: patientId,
          field,
          value,
        }),
      }
    );
    const result = await response.json();
    if (!response.ok || !result.success) {
      return { success: false, error: result.error || "Failed to update field." };
    }
    return result;
  } catch {
    return { success: false, error: "Failed to update field." };
  }
}

function buildUploadConversationEvent(conversationId, totalChunks) {
  return {
    httpMethod: "POST",
    body: JSON.stringify({
      conversation_id: conversationId,
      total_chunks: totalChunks,
    }),
  };
}

function buildSummarizeLambdaEvent(patientId, transcribeJob) {
  const transcribeJobName =
    transcribeJob?.transcribe_job_name || createTranscribeJobName(patientId);

  return {
    Records: [
      {
        body: JSON.stringify({
          Type: "Notification",
          Message: `Job:${transcribeJobName} has finished`,
          MessageAttributes: {
            transcribe_job_name: {
              Type: "String",
              Value: transcribeJobName,
            },
            patient_id: {
              Type: "String",
              Value: String(patientId),
            },
          },
        }),
      },
    ],
  };
}

async function invokeSupportLambda(functionKey, eventPayload, { patientId } = {}) {
  const invokeResponse = await customFetch(
    `${environment.apiUrl}/oncall/invoke-lambda`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        functionKey,
        eventPayload,
        // Passed alongside the event so the server can enforce the Locked-note
        // guard without parsing the Lambda-specific event structure.
        ...(patientId ? { patient_id: String(patientId) } : {}),
      }),
    }
  );
  const invokeResult = await invokeResponse.json();

  if (!invokeResponse.ok || !invokeResult.success) {
    return {
      success: false,
      error:
        invokeResult.error ||
        invokeResult.message ||
        `Failed to invoke ${functionKey}`,
    };
  }

  return { success: true, result: invokeResult.result };
}

/**
 * Describes one invocation so the caller can follow it in CloudWatch.
 * @param {string} functionKey
 * @param {object} result the `result` block from /oncall/invoke-lambda
 */
const toRun = (functionKey, result) => ({
  functionKey,
  functionName: result?.functionName || null,
  requestId: result?.requestId || null,
  status: result?.status || "running",
  startedAt: Date.now(),
});

/**
 * Triggers UploadConversationFunction then RunChatGPTSummarizeFunction for a patient.
 *
 * Lock enforcement chain (no client-side preflight — it had a TOCTOU window):
 * 1. patient-lambda-data rejects Locked patients before returning job data.
 * 2. invoke-lambda rejects Locked patients before firing the Lambda.
 * 3. Postgres trigger rejects the Lambda's own UPDATE if the row is Locked.
 * If the trigger failed to install, invoke-lambda returns 503.
 */
export async function generatePatientNote(patientId) {
  if (!patientId) {
    return { success: false, error: "No patient ID" };
  }

  try {
    const dataResponse = await customFetch(
      `${environment.apiUrl}/oncall/patient-lambda-data/${patientId}`,
      { method: "GET" }
    );
    const dataResult = await dataResponse.json();

    if (!dataResponse.ok || !dataResult.success) {
      return {
        success: false,
        error: dataResult.error || "Failed to load patient job data",
      };
    }

    const { transcribeJob, conversationId, totalChunks } = dataResult.data;
    const convId =
      conversationId || transcribeJob?.conversation_id || `${patientId}`;
    const chunks = transcribeJob?.total_chunks || totalChunks || "1";

    // Both invocations are tracked separately: each is its own Lambda run with
    // its own CloudWatch logs.
    const runs = [];

    const uploadResult = await invokeSupportLambda(
      "UploadConversationFunction",
      buildUploadConversationEvent(convId, chunks)
    );
    if (!uploadResult.success) {
      return { ...uploadResult, runs };
    }
    runs.push(toRun("UploadConversationFunction", uploadResult.result));

    const summarizeResult = await invokeSupportLambda(
      "RunChatGPTSummarizeFunction",
      buildSummarizeLambdaEvent(patientId, transcribeJob),
      { patientId },
    );
    if (!summarizeResult.success) {
      return { ...summarizeResult, runs };
    }
    runs.push(toRun("RunChatGPTSummarizeFunction", summarizeResult.result));

    return { success: true, runs };
  } catch {
    return { success: false, error: "Failed to start note generation." };
  }
}

/**
 * Queues MergeAudioChunksFunction for a specific conversation ID.
 */
export async function mergeAudioChunksForConversation(conversationId) {
  if (!conversationId) {
    return { success: false, error: "No conversation ID" };
  }

  try {
    const mergeResult = await invokeSupportLambda(
      "MergeAudioChunksFunction",
      buildMergeAudioChunksEvent(conversationId)
    );
    if (!mergeResult.success) {
      return { ...mergeResult, runs: [] };
    }
    return {
      success: true,
      runs: [toRun("MergeAudioChunksFunction", mergeResult.result)],
    };
  } catch {
    return { success: false, error: "Failed to start transcript generation." };
  }
}

const transcriptMovesUrl = (suffix = "") =>
  `${environment.apiUrl}/transcript-moves${suffix}`;

async function requestTranscriptMove(url, options, fallbackError) {
  try {
    const response = await customFetch(url, options);
    const result = await response.json();
    if (!response.ok || !result.success) {
      return { success: false, error: result.error || fallbackError, move_id: result.move_id };
    }
    return result;
  } catch {
    return { success: false, error: fallbackError };
  }
}

const jsonPost = (body) => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

const toMoveSelectionBody = ({ fromPatientId, toPatientId, fromJobNames, toJobNames, sourceMode, clearNoteFirst }) => ({
  from_patient_id: String(fromPatientId || ""),
  to_patient_id: String(toPatientId || ""),
  from_job_names: fromJobNames || [],
  to_job_names: toJobNames || [],
  ...(sourceMode && { source_mode: sourceMode }),
  ...(clearNoteFirst && { clear_note_first: true }),
});

export function previewTranscriptMove(selection) {
  return requestTranscriptMove(
    transcriptMovesUrl("/preview"),
    jsonPost(toMoveSelectionBody(selection)),
    "Failed to preview the move."
  );
}

export function startTranscriptMove(selection) {
  return requestTranscriptMove(
    transcriptMovesUrl(),
    jsonPost(toMoveSelectionBody(selection)),
    "Failed to replace the transcripts."
  );
}

export async function getTranscriptMoveHistory(patientId) {
  if (!patientId) return { success: true, moves: [] };
  const result = await requestTranscriptMove(
    transcriptMovesUrl("/history"),
    jsonPost({ patient_id: String(patientId) }),
    "Failed to load the move history."
  );
  return result.success ? result : { success: false, error: result.error, moves: [] };
}

export function getTranscriptMove(moveId) {
  return requestTranscriptMove(
    transcriptMovesUrl(`/${moveId}`),
    { method: "GET" },
    "Failed to load the move."
  );
}

export function regenerateMoveTarget(moveId) {
  return requestTranscriptMove(
    transcriptMovesUrl(`/${moveId}/regenerate-target`),
    { method: "POST" },
    "Failed to generate the target patient's note."
  );
}

export function regenerateMoveSource(moveId) {
  return requestTranscriptMove(
    transcriptMovesUrl(`/${moveId}/regenerate-source`),
    { method: "POST" },
    "Failed to generate the source patient's note."
  );
}

export function rollbackTranscriptMove(moveId) {
  return requestTranscriptMove(
    transcriptMovesUrl(`/${moveId}/rollback`),
    { method: "POST" },
    "Failed to roll back the move."
  );
}

export async function getPatientConversationIds(patientId) {
  return fetchPatientConversationIds(patientId);
}

/** @deprecated Use getPatientConversationIds instead. */
export async function lookupPatientAudioChunks({
  doctorName,
  month,
  day,
  patientId,
}) {
  if (!doctorName || !month || !day || !patientId) {
    return { success: false, error: "Doctor, date, and patient ID are required." };
  }

  try {
    const response = await customFetch(
      `${environment.apiUrl}/oncall/audio-lookup`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          doctor_name: doctorName,
          month,
          day,
          patient_id: patientId,
        }),
      }
    );
    const result = await response.json();

    if (!response.ok || !result.success) {
      return {
        success: false,
        error: result.error || result.message || "Audio lookup failed",
      };
    }

    return {
      success: true,
      conversations: Array.isArray(result.data) ? result.data : [],
      searchPrefix: result.search_prefix || "",
    };
  } catch {
    return { success: false, error: "Failed to look up audio chunks." };
  }
}

/** @deprecated Use mergeAudioChunksForConversation with a chosen conversation ID. */
export async function generatePatientTranscript(patientId) {
  if (!patientId) {
    return { success: false, error: "No patient ID" };
  }

  try {
    const dataResponse = await customFetch(
      `${environment.apiUrl}/oncall/patient-lambda-data/${patientId}`,
      { method: "GET" }
    );
    const dataResult = await dataResponse.json();

    if (!dataResponse.ok || !dataResult.success) {
      return {
        success: false,
        error: dataResult.error || "Failed to load patient job data",
      };
    }

    const { conversationId } = dataResult.data;
    return mergeAudioChunksForConversation(
      conversationId || `${patientId}`
    );
  } catch {
    return { success: false, error: "Failed to start transcript generation." };
  }
}

export async function uploadPatientNoteTo4d(patientId) {
  if (!patientId) {
    return { success: false, error: "No patient ID" };
  }

  try {
    const response = await customFetch(
      `${environment.apiUrl}/support/patients/${encodeURIComponent(patientId)}/upload-4d`,
      { method: "POST" }
    );
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) {
      return {
        success: false,
        error: result.error || "Failed to upload note to 4D",
      };
    }
    return { success: true };
  } catch {
    return { success: false, error: "Failed to upload note to 4D." };
  }
}

export async function submitSupportFeedback(patientId, feedback, ticketId) {
  const trimmed = feedback?.trim();
  if (!trimmed) {
    return { success: false, error: "Feedback is required" };
  }

  try {
    const response = await customFetch(
      `${environment.apiUrl}/support/feedback`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: patientId ?? null,
          ticket_id: ticketId ?? null,
          feedback: trimmed,
          submitted_by: localStorage.getItem("username") || null,
        }),
      }
    );
    const result = await response.json();

    if (!response.ok || !result.success) {
      return {
        success: false,
        error: result.error || "Failed to submit feedback",
      };
    }

    return { success: true };
  } catch {
    return { success: false, error: "Failed to submit feedback." };
  }
}

export async function getPatientsByDoctorAndDate(doctorId, dateOfAppt) {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/patients/summary/by-doctor-and-date?doctor_id=${encodeURIComponent(doctorId)}&date_of_appt=${encodeURIComponent(dateOfAppt)}`,
      { method: "GET" }
    );
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}
